import React from "react";
import { ColDef } from "ag-grid-community";
import Document from "../../../models/document";
import Tag from "../../../models/tag";
import TagsCell from "../TagsCell";
import { requiredFields } from "./constants";

interface BuildDocumentMetadataColumnsParams {
  changedCells: { [key: number]: Set<string> };
  deletingIds: number[];
  formatDateForInput: (dateString?: string | null) => string;
  handleOpenVersionsModal: (document: Document) => void;
  isExpired: (expirationDate?: string) => boolean;
  markCellChanged: (rowId: number, field?: string) => void;
  setChangedCells: React.Dispatch<
    React.SetStateAction<{ [key: number]: Set<string> }>
  >;
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  setGridDateValue: (params: any, field: string, value: string | null) => void;
  stopGridMouseEvent: (event: React.SyntheticEvent) => void;
  tags: Tag[];
  confirmDelete: (id: number) => void;
}

export const buildDocumentMetadataColumns = ({
  changedCells,
  deletingIds,
  formatDateForInput,
  handleOpenVersionsModal,
  isExpired,
  markCellChanged,
  setChangedCells,
  setDocuments,
  setGridDateValue,
  stopGridMouseEvent,
  tags,
  confirmDelete,
}: BuildDocumentMetadataColumnsParams): ColDef[] => [
  {
    field: "name",
    headerName: "Nombre",
    editable: true,
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "name") &&
        (params.data.name === undefined ||
          params.data.name === null ||
          params.data.name === ""),
    },
  },
  {
    field: "documentType",
    headerName: "Tipo de Documento",
    filter: true,
    filterValueGetter: (params) => {
      if (!params.data) return "";
      const value = params.data.documentType;
      if (value === "digital") return "Digital";
      if (value === "physical") return "Físico";
      return value;
    },
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "documentType") &&
        (params.data.documentType === undefined ||
          params.data.documentType === null ||
          params.data.documentType === ""),
    },
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedValue = e.target.value;
        params.setValue(updatedValue);

        setChangedCells((prev) => {
          const newChanges = { ...prev };
          if (!newChanges[params.data.id]) {
            newChanges[params.data.id] = new Set();
          }
          newChanges[params.data.id].add("documentType");
          return newChanges;
        });
      };

      return (
        <select
          value={params.value}
          onChange={handleChange}
          style={{
            width: "100%",
            padding: "5px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <option value="digital">Digital</option>
          <option value="physical">Físico</option>
        </select>
      );
    },
  },
  {
    field: "typeName",
    headerName: "Tipo de Activo",
    editable: false,
    filter: true,
    width: 150,
    cellRenderer: (params: any) => params.value || "-",
  },
  {
    field: "physicalCopyRequired",
    headerName: "Copia Física Requerida",
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
    },
    width: 180,
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedValue = e.target.checked;
        params.setValue(updatedValue);
        setChangedCells((prev) => {
          const newChanges = { ...prev };
          if (!newChanges[params.data.id]) {
            newChanges[params.data.id] = new Set();
          }
          newChanges[params.data.id].add("physicalCopyRequired");
          return newChanges;
        });
      };

      return (
        <input
          type="checkbox"
          checked={params.value || false}
          onChange={handleChange}
          style={{
            width: "16px",
            height: "16px",
            cursor: "pointer",
          }}
        />
      );
    },
  },
  {
    field: "expirationDate",
    headerName: "Vto",
    editable: false,
    filter: true,
    width: 180,
    cellClass: (params: any) => {
      if (!params.data) return "";
      const classes = [];

      if (changedCells[params.data.id]?.has(params.colDef.field ?? "")) {
        classes.push("cell-changed");
      }

      if (isExpired(params.value)) {
        classes.push("cell-expired");
      }

      return classes;
    },
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedValue = e.target.value || null;
        setGridDateValue(params, "expirationDate", updatedValue);
        markCellChanged(params.data.id, "expirationDate");
      };

      return (
        <input
          type="date"
          defaultValue={formatDateForInput(params.value)}
          onChange={handleDateChange}
          onMouseDown={stopGridMouseEvent}
          onKeyDown={stopGridMouseEvent}
          placeholder="YYYY-MM-DD"
          title="Formato: YYYY-MM-DD"
          style={{
            width: "100%",
            padding: "0px",
            border: "0px solid #ccc",
            borderRadius: "0px",
            backgroundColor: "transparent",
          }}
        />
      );
    },
  },
  {
    field: "expirationNoticeDays",
    headerName: "Anticipación de vencimiento (días)",
    editable: true,
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "expirationNoticeDays") &&
        (params.data.expirationNoticeDays === undefined ||
          params.data.expirationNoticeDays === null ||
          params.data.expirationNoticeDays === ""),
    },
  },
  {
    field: "expirationGraceDays",
    headerName: "Tolerancia (días)",
    editable: true,
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "expirationGraceDays") &&
        (params.data.expirationGraceDays === undefined ||
          params.data.expirationGraceDays === null ||
          params.data.expirationGraceDays === ""),
    },
  },
  {
    field: "expirationReminderRepeatDays",
    headerName: "Repetición de alarma de vencimiento (días)",
    editable: true,
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "expirationReminderRepeatDays") &&
        (params.data.expirationReminderRepeatDays === undefined ||
          params.data.expirationReminderRepeatDays === null ||
          params.data.expirationReminderRepeatDays === ""),
    },
  },
  {
    field: "version",
    headerName: "Versión",
    editable: false,
    filter: true,
    width: 320,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      "cell-invalid": (params) =>
        params.data &&
        requiredFields.some((f) => f.field === "version") &&
        (params.data.version === undefined ||
          params.data.version === null ||
          params.data.version === ""),
    },
  },
  {
    field: "tags",
    headerName: "Tags",
    filter: "agTextColumnFilter",
    sortable: false,
    resizable: true,
    width: 250,
    filterValueGetter: (params) => {
      const tagNames = ((params.data?.tags) || [])
        .map((tag: Tag) => tag.name)
        .join(", ");
      return tagNames;
    },
    filterParams: {
      textFormatter: (searchTerm: string) => {
        return searchTerm.toLowerCase();
      },
    },
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
    },
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const currentTags = params.data?.tags || [];
      const rowId = params.data.id;

      const handleTagsChange = (selectedTags: Tag[]) => {
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === rowId ? { ...doc, tags: selectedTags } : doc,
          ),
        );

        setChangedCells((prev) => {
          const updated = { ...prev };
          if (!updated[rowId]) {
            updated[rowId] = new Set();
          }
          updated[rowId].add("tags");
          return updated;
        });
      };

      return (
        <TagsCell
          currentTags={currentTags}
          allTags={tags}
          onTagsChange={handleTagsChange}
        />
      );
    },
  },
  {
    field: "versions",
    headerName: "Versiones",
    editable: false,
    filter: false,
    width: 120,
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const document = params.data;
      const hasVersions = document.id && document.id > 0;

      return (
        <div style={{ textAlign: "center" }}>
          <button
            className="btn btn-outline-info btn-sm"
            onClick={() => handleOpenVersionsModal(document)}
            disabled={!hasVersions}
            title={hasVersions ? "Ver versiones" : "Guardar documento primero"}
            style={{
              padding: "3px 6px",
              minWidth: "30px",
              opacity: hasVersions ? 1 : 0.5,
            }}
          >
            <i className="fa fa-history"></i>
          </button>
        </div>
      );
    },
  },
  {
    field: "actions",
    headerName: "Acciones",
    editable: false,
    filter: false,
    width: 120,
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const isTemporary = params.data.id < 0;
      const isDeleting = deletingIds.includes(params.data.id);

      return (
        <div style={{ textAlign: "center" }}>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => confirmDelete(params.data.id)}
            disabled={isDeleting}
            title={isTemporary ? "Eliminar fila" : "Eliminar documento"}
            style={{
              padding: "3px 6px",
              minWidth: "30px",
              opacity: isDeleting ? 0.5 : 1,
            }}
          >
            {isDeleting ? (
              <i className="fa fa-spinner fa-spin"></i>
            ) : (
              <i className="fa fa-trash"></i>
            )}
          </button>
        </div>
      );
    },
  },
];
